import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'Components/Atoms/Button/Button';
import { mapDtoToProduct } from 'Utils/mappers';
import classes from './ProviderConnectionsErrorManager.module.css';
import clsx from 'clsx';
import Badge from 'Components/Atoms/Badge/Badge';
import { confirmProviderConnection } from 'Services/providersConnections';
import { useQueryClient } from '@tanstack/react-query';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import { useTranslation } from 'react-i18next';

export default function CreatingPartialMatch(props) {
    const { providerConnection, description, onClose, onClearConnection } = props;
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const queryClient = useQueryClient();
    const { showUnknownError } = useErrors();

    const { t } = useTranslation('providers');

    const handleReject = async () => {
        setProcessing(true);
        await onClearConnection();
        onClose();
    };

    const productsChanged = useMemo(() => {
        if (description === 'reconnecting_partial_match') {
            const { old_products, new_products } = providerConnection.status_message.info;
            return [
                ...new_products.map((p) => ({
                    ...mapDtoToProduct(p),
                    isNewProduct: true,
                })),
                ...old_products.map((p) => ({
                    ...mapDtoToProduct(p),
                    isOldProduct: true,
                })),
            ];
        }

        if (description === 'creating_partial_match') {
            const { common_products } = providerConnection.status_message.info;
            return common_products.map(mapDtoToProduct);
        }

        return [];
    }, [providerConnection, description]);

    const confirmCreationPartialMatch = async () => {
        if (processing) return;

        setLoading(true);

        try {
            await confirmProviderConnection(providerConnection.id);
            await queryClient.invalidateQueries({ queryKey: ['providers-connections'] });
            onClose();
        } catch (error) {
            showUnknownError(error);
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const getProductDescription = (product) => {
        switch (product.type) {
            case 'checking':
                return t('product-checking');
            case 'investment':
            case 'pensionplan':
            case 'stocks':
            case 'securities':
            case 'saving':
                return t('product-investment');
            case 'card':
            case 'debit':
                return t('product-card');
            case 'loan':
            case 'lineofcredit':
                return t('product-loan');
            default:
                return t('product-others');
        }
    };

    return (
        <div className="text-center">
            <div className="flex items-center justify-center">
                <img
                    src={providerConnection.logo_url}
                    alt={providerConnection.name}
                    className="ml-4 shadow-lg rounded-full"
                    style={{ height: '4rem' }}
                />
                <img alt="" src="/img/arrows-connecting.svg" className={'ml-6'} style={{ height: '2rem' }} />
                <img
                    alt=""
                    src="/img/bt-icon.png"
                    className={'ml-6 shadow-lg rounded-full'}
                    style={{ height: '4rem' }}
                />
            </div>

            <h3 className="font-medium mt-12">{t('creating-partial-match-title')}</h3>
            <div className="mt-2 text-gray-500">{t('creating-partial-match-description')}</div>

            <div className="my-4">
                {productsChanged.map((product, index) => (
                    <div className={classes.productRow} key={`product-${index}`}>
                        <div className="flex gap-4 items-center justify-between text-left">
                            <div
                                className={clsx(
                                    { [classes.isDeleted]: product.isOldProduct },
                                    'flex',
                                    'gap-2',
                                    'items-center',
                                    'overflow-hidden',
                                )}
                            >
                                <div className="flex-shrink-1">
                                    <img src={product.icon} alt={product.type} width={32} />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <div className="font-bold text-truncate">{product.name}</div>

                                    <div className="text-xs text-gray-500">{getProductDescription(product)}</div>
                                </div>
                            </div>

                            <div className="font-bold">
                                <Badge color="black">{t('duplicated')}</Badge>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-6 text-gray-500">{t('creating-partial-match-confirm')}</div>

            <div className="mt-6 flex gap-4 justify-center">
                <Button variant="primary" isBusy={loading} onClick={confirmCreationPartialMatch}>
                    {t('yes-continue')}
                </Button>
                <Button variant="default" isBusy={processing} onClick={handleReject}>
                    {t('no')}
                </Button>
            </div>
        </div>
    );
}

CreatingPartialMatch.propTypes = {
    providerConnection: PropTypes.object.isRequired,
    description: PropTypes.string.isRequired,
    info: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onClearConnection: PropTypes.func.isRequired,
};
